import { Component, type ErrorInfo } from 'react';
import { Button } from '@/components/ui/button';
import isChunkLoadError from './helpers/isChunkLoadError';
import reloadCurrentPage from './helpers/reloadCurrentPage';
import { ChunkLoadFailureMessage, ChunkLoadFailureRegion, ChunkLoadFailureTitle } from './styled';
import type { ChunkLoadErrorBoundaryProps, ChunkLoadErrorBoundaryState } from './types';

class ChunkLoadErrorBoundary extends Component<
    ChunkLoadErrorBoundaryProps,
    ChunkLoadErrorBoundaryState
> {
    public state: ChunkLoadErrorBoundaryState = {
        error: null,
    };

    public static getDerivedStateFromError(error: Error): ChunkLoadErrorBoundaryState {
        return { error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        if (isChunkLoadError({ error })) {
            globalThis.console.error('A lazy page chunk failed to load:', error, errorInfo);
        }
    }

    public render() {
        const { error } = this.state;

        if (!error) {
            return this.props.children;
        }

        if (!isChunkLoadError({ error })) {
            throw error;
        }

        return (
            <ChunkLoadFailureRegion role="alert">
                <ChunkLoadFailureTitle>A new version is available</ChunkLoadFailureTitle>
                <ChunkLoadFailureMessage>
                    Reload the page to continue with the latest version.
                </ChunkLoadFailureMessage>
                <Button type="button" onClick={reloadCurrentPage}>
                    Reload page
                </Button>
            </ChunkLoadFailureRegion>
        );
    }
}

export default ChunkLoadErrorBoundary;
